package com.wintermar.inspection.core.ui.component.button

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import com.wintermar.inspection.core.ui.component.TextBodySmall
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.leading_icon_button
import inspection.composeapp.generated.resources.trailing_icon_button
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
private fun InputButtonBase(
    modifier: Modifier = Modifier,
    leadingIcon: DrawableResource? = null,
    trailingIcon: DrawableResource? = null,
    disabled: Boolean = false,
    disabledClick: Boolean = false,
    onClick: () -> Unit,
    content: @Composable () -> Unit,
) {
    val color = LocalAppTheme.current.color
    val dimension = LocalAppTheme.current.dimension

    Row(
        modifier = modifier
            .background(
                color = if (disabled) color.surface.input.disabled else color.surface.input.default,
                shape = RoundedCornerShape(dimension.radius.md)
            )
            .clip(shape = RoundedCornerShape(dimension.radius.md))
            .clickable(
                enabled = !disabled && !disabledClick,
                onClick = onClick
            )
            .border(
                width = 1.dp,
                color = color.border.default,
                shape = RoundedCornerShape(dimension.radius.md)
            )
            .padding(
                horizontal = dimension.spacing.space4,
                vertical = dimension.spacing.space3
            ),
        horizontalArrangement = Arrangement.spacedBy(space = dimension.spacing.space2),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (leadingIcon != null) {
            Icon(
                painter = painterResource(leadingIcon),
                modifier = Modifier.size(dimension.size.icon.s),
                tint = if (disabled) color.icon.disabled else color.icon.subtlest,
                contentDescription = stringResource(Res.string.leading_icon_button)
            )
        }

        Column(modifier = Modifier.weight(1f)) {
            content()
        }

        if (trailingIcon != null) {
            Icon(
                painter = painterResource(trailingIcon),
                modifier = Modifier.size(dimension.size.icon.s),
                tint = if (disabled) color.icon.disabled else color.icon.subtlest,
                contentDescription = stringResource(Res.string.trailing_icon_button)
            )
        }
    }
}

@Composable
fun InputButton(
    text: String = "",
    placeholder: String = "",
    modifier: Modifier = Modifier,
    leadingIcon: DrawableResource? = null,
    trailingIcon: DrawableResource? = null,
    disabled: Boolean = false,
    disabledClick: Boolean = false,
    onClick: () -> Unit,
) {
    val color = LocalAppTheme.current.color

    InputButtonBase(
        modifier = modifier,
        leadingIcon = leadingIcon,
        trailingIcon = trailingIcon,
        disabled = disabled,
        disabledClick = disabledClick,
        onClick = onClick,
    ) {
        TextBodySmall(
            text = text.ifEmpty { placeholder },
            color = { textColor ->
                when {
                    disabled -> textColor.disabled
                    text.isEmpty() -> textColor.subtlest
                    else -> color.text.default
                }
            }
        )
    }
}

@Composable
fun InputButton(
    modifier: Modifier = Modifier,
    leadingIcon: DrawableResource? = null,
    trailingIcon: DrawableResource? = null,
    disabled: Boolean = false,
    disabledClick: Boolean = false,
    onClick: () -> Unit,
    content: @Composable () -> Unit,
) {
    InputButtonBase(
        modifier = modifier,
        leadingIcon = leadingIcon,
        trailingIcon = trailingIcon,
        disabled = disabled,
        disabledClick = disabledClick,
        onClick = onClick,
        content = content,
    )
}
